import * as Sentry from "@sentry/react";
import { useUserStore } from "../stores/user.store";
import { useAuthStore } from "../stores/auth.store";
import { getPWAStatus } from "./pwaDetection";

interface SentryContext {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  level?: "fatal" | "error" | "warning" | "log" | "info" | "debug";
}

/**
 * Enriches Sentry context with user information if available
 * @param context - The original context to be enriched
 * @returns The enriched context with user information
 */
function enrichWithUserContext(context: SentryContext = {}): SentryContext {
  const authUser = useAuthStore.getState().authUser;
  const profile = useUserStore.getState().profile;

  const enrichedExtra: Record<string, unknown> = {
    ...context.extra,
  };

  // Add user information if available
  if (authUser) {
    enrichedExtra.userId = authUser.id;
    enrichedExtra.userEmail = authUser.email;

    // Also set Sentry user context for better tracking
    Sentry.setUser({
      id: authUser.id,
      email: authUser.email,
    });
  }

  // Add profile information if available
  if (profile) {
    enrichedExtra.profileId = profile.id;
    const profileOwner = profile.members.find((m) => m.is_owner);
    enrichedExtra.memberId = profileOwner?.id;
    enrichedExtra.profileName = `${profileOwner?.first_name} ${profileOwner?.last_name}`;
    enrichedExtra.memberCount = profile.members?.length || 0;
  }

  return {
    ...context,
    extra: enrichedExtra,
  };
}

/**
 * Captures an exception with automatic user context enrichment
 * @param error - The error to capture
 * @param context - Additional context for the error
 */
export function captureException(
  error: unknown,
  context?: SentryContext,
): void {
  const enrichedContext = enrichWithUserContext(context);
  Sentry.captureException(error, enrichedContext);
}

/**
 * Captures a message with automatic user context enrichment
 * @param message - The message to capture
 * @param context - Additional context for the message
 */
export function captureMessage(message: string, context?: SentryContext): void {
  const enrichedContext = enrichWithUserContext(context);
  Sentry.captureMessage(message, enrichedContext);
}

/**
 * Sets user context in Sentry when user logs in
 * Should be called after successful authentication
 */
export function setUserContext(): void {
  const authUser = useAuthStore.getState().authUser;
  const profile = useUserStore.getState().profile;
  const profileOwner = profile?.members.find((m) => m.is_owner);
  const pwaStatus = getPWAStatus();

  if (authUser) {
    Sentry.setUser({
      id: authUser.id,
      email: authUser.email,
      username: profile
        ? `${profileOwner?.first_name} ${profileOwner?.last_name}`
        : undefined,
    });

    // Also set PWA-related tags for this user
    Sentry.setTags({
      'user.app_mode': pwaStatus.isInstalled ? 'pwa' : 'browser',
      'user.platform': pwaStatus.platform,
    });
  }
}

/**
 * Clears user context in Sentry when user logs out
 * Should be called after logout
 */
export function clearUserContext(): void {
  Sentry.setUser(null);
}
